import {
  FlexBox,
  SelectionListChangeEvent,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { TagChips } from 'lib-shared-tagging';
import { ampli } from 'lib-shared-tracking';
import type { Tag } from 'lib-shared-types-api';
import { useCallback } from 'react';

import { conversationsApi } from 'apis/conversations';

import { TagsDropdownButton } from './components/tagsDropdownButton/tagsDropdownButton';
import styles from './conversationTags.module.scss';

type ConversationTagsProps = {
  /** The id of the conversation. */
  csid: number;
};

/** Represents a component that presents the conversation tags. */
export const ConversationTags = ({
  csid,
}: ConversationTagsProps): JSX.Element => {
  const hasTagAccess = useHasAccess('conversation.tag');
  const { data, isFetching } = conversationsApi.useReadConversationQuery(
    {
      csid,
    },
    { refetchOnMountOrArgChange: true },
  );
  const [updateConversationTag] =
    conversationsApi.useUpdateConversationTagMutation();

  const handleChange = useCallback(
    ({
      selectedItem: { isSelected, value: tag },
    }: SelectionListChangeEvent<Tag>) => {
      if (isSelected) {
        updateConversationTag({ action: 'add', csid, tag });
        ampli.conversationHeaderTagAdded({ csid, tag: tag.name });
      } else {
        updateConversationTag({ action: 'remove', csid, tag });
        ampli.conversationHeaderTagRemoved({ csid, tag: tag.name });
      }
    },
    [csid, updateConversationTag],
  );

  const handleRemove = useCallback(
    (tag: Tag) => {
      updateConversationTag({ action: 'remove', csid, tag });
      ampli.conversationHeaderTagRemoved({ csid, tag: tag.name });
    },
    [csid, updateConversationTag],
  );

  const tags = data?.data.history.main_conversation.tags ?? [];

  return (
    <FlexBox alignItems="center" gap="100" className={styles.tags}>
      <Tooltip
        content="You don't have permissions to add tags"
        disabled={hasTagAccess}
      >
        <TagsDropdownButton
          tags={tags}
          disabled={!hasTagAccess}
          isLoading={isFetching}
          onChange={handleChange}
        />
      </Tooltip>
      <TagChips
        tags={tags}
        limit={3}
        onRemove={hasTagAccess ? handleRemove : undefined}
        tagClassName={styles.tag}
      />
    </FlexBox>
  );
};
